export type PeriodPreset = 'LAST_7_DAYS' | 'LAST_30_DAYS' | 'LAST_90_DAYS' | 'ALL_TIME';

export interface PeriodOption {
  readonly value: PeriodPreset;
  readonly label: string;
  /** Finishes a sentence about the list: "Nenhum pedido pendente nos últimos 30 dias". */
  readonly phrase: string;
}

/** Calendar days as YYYY-MM-DD, both ends inclusive. */
export interface DateRange {
  readonly from: string;
  readonly to: string;
}

export const PERIOD_OPTIONS: readonly PeriodOption[] = [
  { value: 'LAST_7_DAYS', label: 'Últimos 7 dias', phrase: 'nos últimos 7 dias' },
  { value: 'LAST_30_DAYS', label: 'Últimos 30 dias', phrase: 'nos últimos 30 dias' },
  { value: 'LAST_90_DAYS', label: 'Últimos 90 dias', phrase: 'nos últimos 90 dias' },
  { value: 'ALL_TIME', label: 'Todo o período', phrase: '' }
];

const DAYS_BY_PRESET: Readonly<Record<Exclude<PeriodPreset, 'ALL_TIME'>, number>> = {
  LAST_7_DAYS: 7,
  LAST_30_DAYS: 30,
  LAST_90_DAYS: 90
};

/**
 * The calendar days a preset covers, today being the last of them: "últimos 7 dias" is today and
 * the six days before it. Dates are read in the browser's local time zone. `null` means unbounded.
 */
export function periodRange(preset: PeriodPreset, today: Date): DateRange | null {
  if (preset === 'ALL_TIME') {
    return null;
  }

  // Built from the date parts, so month and year boundaries roll over and no clock time or DST shift gets in.
  const from = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - (DAYS_BY_PRESET[preset] - 1)
  );

  return { from: toLocalIsoDate(from), to: toLocalIsoDate(today) };
}

export function periodPhrase(preset: PeriodPreset): string {
  return PERIOD_OPTIONS.find(option => option.value === preset)?.phrase ?? '';
}

/** YYYY-MM-DD of the local calendar day. `toISOString()` gives the UTC day: in Brazil, tomorrow after 21h. */
export function toLocalIsoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}
